import { AppContext } from '../../common/AppContext';
import { CountrySelectorList } from '../../data/filters/CountrySelectorList';
import type {
  AbstractFilterSelectProps,
  BaseSelectorOption,
} from '../../types/components/selector.types';
import { AbstractFilterSelect } from './AbstractFilterSelect';

interface CountrySelectorOption extends BaseSelectorOption {
  twoLetterISO: string;
}
export class CountrySelector<
  TOption extends CountrySelectorOption = CountrySelectorOption,
> extends AbstractFilterSelect<TOption> {
  static contextType = AppContext;

  static defaultProps = {
    ...AbstractFilterSelect.defaultProps,
    label: 'Country',
    listType: 'Countries',
    initialSearch: true,
    maxResults: 9999,
    callApi: false,
    multiple: false,
  };

  constructor(props: AbstractFilterSelectProps<TOption>) {
    super(props);

    this.dataList = CountrySelectorList as unknown as typeof this.dataList;
  }

  getItemId(c: TOption) {
    return c ? c.twoLetterISO : '';
  }

  findValueItem(items: TOption[], value: string): TOption | null {
    return items ? (items.find((c) => c.name === value) ?? null) : null;
  }

  getItemLabel(item: TOption) {
    const { getItemLabel } = this.props;

    if (getItemLabel) return getItemLabel(item);
    return item.name || '';
  }

  getList(dataList: CountrySelectorList) {
    return dataList.countries;
  }

  // AbstractFilterSelect's default getData calls `dataList.get(context, value)`, but `dataList`
  // here is the CountrySelectorList class (not an instance), so that routes to the inherited
  // static Data.get and treats the search string as a URL path — hence the spurious /api 404.
  // Override with the named static loader that hits the real SupportedCountries endpoint.
  getData(value: string | null): Promise<unknown> {
    return CountrySelectorList.getCountrySelectorList(this.context, value);
  }

  // The base clearResults() empties the in-memory options whenever the input goes blank (the
  // clear button or typing-to-empty). That's fine for an API-backed selector because the next
  // keystroke refetches, but the country list is a cached, client-side-filtered singleton loaded
  // once with callApi=false — so nothing ever repopulates it and the dropdown stays permanently
  // empty after a clear. Reload the cached list instead so an empty query shows the full set of
  // countries, matching standard autocomplete behaviour.
  clearResults(): void {
    this.ignorePendingResults = false;
    this.loadItems(null, true);
  }
}
